import { useHudStore } from "../../store/hudStore";
import { bloom, fill } from "../ui";

// Certifications module for the Learning domain page. The exams behind
// [[The Golden Jacket]], which is "every AWS certification, held at once".
//
// The jacket is a standard to hold rather than a box to tick: certs expire, so
// the number that matters is how many are held RIGHT NOW, not how many were
// ever passed. That is why the headline counts `passed` against the full set
// and the list puts unfinished work first.
//
// Read-only by design. Moving a cert to `passed` is worth 1000 XP and the
// `cert-earned` badge (System/xp-rules.md), so that write belongs with the
// completion engine, not with a display module.

const TONE: Record<string, { label: string; color: string }> = {
  passed: { label: "Held", color: "var(--green-ink)" },
  studying: { label: "In flight", color: "var(--accent-ink)" },
  planned: { label: "Queued", color: "var(--mut)" },
};

// `goal` scopes the module to one main quest's exams, which is how the Golden
// Jacket page uses it. Unscoped (the Learning domain page) it shows every cert.
export default function CertsModule({ goal }: { goal?: string } = {}) {
  const st = useHudStore();
  const certs = goal ? st.certs.filter((c) => c.goal === goal) : st.certs;
  const held = certs.filter((c) => c.status === "passed").length;
  const flight = certs.find((c) => c.status === "studying");
  const pct = certs.length ? Math.round((held / certs.length) * 100) : 0;

  const headline = held === certs.length && certs.length > 0
    ? "Every certification held. That is the jacket."
    : held === 0
      ? "None held yet. " + certs.length + " exams between here and the jacket."
      : held + " of " + certs.length + " held at once.";
  const sub = flight
    ? "In flight: " + flight.name + (flight.target ? ", targeting " + flight.target : "")
    : "Nothing in flight. Pick the next exam and give it a target date.";

  return (
    <div style={{ background: "var(--card)", border: "1px solid var(--line)", borderRadius: 10, padding: "20px clamp(16px,2vw,22px) 22px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--mut)" }}>Certifications</div>
          <div style={{ fontSize: "clamp(20px,3vw,26px)", fontWeight: 700, fontFamily: "var(--display)", letterSpacing: "-0.01em", lineHeight: 1.2, marginTop: 10, textWrap: "pretty" }}>{headline}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mut)", marginTop: 7, textWrap: "pretty" }}>{sub}</div>
        </div>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 11.5, fontWeight: 700, color: "var(--mut)", background: "var(--card2)", border: "1px solid var(--line)", padding: "8px 13px", borderRadius: 999 }}>
          <i className="fa-solid fa-certificate" style={{ fontSize: 11, lineHeight: 1, color: "var(--gold)" }} />held at once, not ever passed
        </span>
      </div>

      <div style={{ height: 8, borderRadius: 4, background: "var(--raise)", overflow: "hidden", marginTop: 18 }}>
        <span style={{ ...fill(pct), borderRadius: 4, background: "var(--gold)", boxShadow: bloom("var(--gold)", pct, 10) }} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 12, marginTop: 18 }}>
        {certs.map((c) => {
          const tone = TONE[c.status] ?? TONE.planned!;
          const done = c.status === "passed";
          return (
            <div key={c.id} style={{ display: "flex", flexDirection: "column", gap: 10, padding: 15, borderRadius: 10, background: "var(--card2)", border: `1px solid ${done ? "var(--gold)" : "var(--line)"}` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: tone.color }}>{tone.label}</span>
                {c.exam_code && <span style={{ fontFamily: "var(--mono, monospace)", fontSize: 11, fontWeight: 700, color: "var(--mut)" }}>{c.exam_code}</span>}
              </div>
              <div style={{ fontSize: 13.5, fontWeight: 700, fontFamily: "var(--display)", letterSpacing: "0", lineHeight: 1.3, textWrap: "pretty", color: done ? "var(--ink)" : "var(--sub)" }}>{c.name}</div>
              {c.status === "studying" && (
                <div style={{ height: 6, borderRadius: 999, background: "var(--raise)", overflow: "hidden" }}>
                  <span style={{ display: "block", height: "100%", borderRadius: 999, background: "var(--accent)", width: Math.max(0, Math.min(100, c.progress)) + "%" }} />
                </div>
              )}
              <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--mut)", marginTop: "auto" }}>
                {done ? (c.passed_on ? "passed " + c.passed_on : "passed") : c.target ? "target " + c.target : "no date set"}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--mut)", marginTop: 16, textWrap: "pretty" }}>Authored in the vault under Trackers/Certifications. Passing one is worth 1000 XP to Knowledge and lights the Cert earned badge.</div>
    </div>
  );
}
